import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { PATENT_DRAWER_TABS } from '../../constants/constants';

interface AppState {
  collapsed: boolean;
  sessionID: string;
  defaultTab: PATENT_DRAWER_TABS;
  drawerSuggestionsOpen: boolean;
  patentabilityAnalysisDrawerOpen: boolean;
  selectedNodeId: string | null;
  drawerScore: number | null;
  drawerFilterOpen: boolean;
  drawerUserInfoOpen: boolean;
  drawerFilterId: string | null;
  searchFromHistoryEnabled: boolean;
  searchFromHistoryID: string;
  searchFromHistoryFilters: { field: string; values: string[] }[];
  searchFromHistoryDateRangeFilters: {
    filterField: string;
    fromDate: string;
    toDate: string;
  }[];
  searchFromHistoryCPCFilters: { field: string; value: string }[];
  searchFromHistoryPatentId: string | null;
  exportPatents: string[];
  exportColumns: string[];
  searchType: 'patent' | 'chemistry';
  lastOpenedDrawer: string | null;
}

const initialState: AppState = {
  collapsed: false,
  sessionID: '',
  defaultTab: PATENT_DRAWER_TABS.SUMMARY,
  drawerUserInfoOpen: false,
  drawerScore: null,
  drawerFilterOpen: false,
  drawerSuggestionsOpen: false,
  patentabilityAnalysisDrawerOpen: false,
  selectedNodeId: null,
  drawerFilterId: null,
  searchFromHistoryEnabled: false,
  searchFromHistoryID: '',
  searchFromHistoryFilters: [],
  searchFromHistoryDateRangeFilters: [],
  searchFromHistoryCPCFilters: [],
  searchFromHistoryPatentId: null,
  exportPatents: [],
  exportColumns: [],
  searchType: 'patent',
  lastOpenedDrawer: null,
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setCollapsed: (state, action: PayloadAction<boolean>) => {
      state.collapsed = action.payload;
    },
    setSessionID: (state, action: PayloadAction<string>) => {
      state.sessionID = action.payload;
    },
    setDefaultTab: (state, action: PayloadAction<PATENT_DRAWER_TABS>) => {
      state.defaultTab = action.payload;
    },
    setLastOpenedDrawer: (state, action: PayloadAction<string | null>) => {
      state.lastOpenedDrawer = action.payload;
    },
    addPatentsToExport: (state, action: PayloadAction<string | string[]>) => {
      if (typeof action.payload !== 'string') {
        state.exportPatents = [...state.exportPatents, ...action.payload.filter(p => !state.exportPatents.includes(p))];
      } else {
        state.exportPatents = [...state.exportPatents, action.payload];
      }
    },
    removePatentsToExport: (state, action: PayloadAction<string | string[]>) => {
      if (typeof action.payload !== 'string') {
        state.exportPatents = [...state.exportPatents.filter(i => !action.payload.includes(i))];
      } else {
        state.exportPatents = [...state.exportPatents.filter(i => i !== action.payload)];
      }
    },
    clearPatentToExport: state => {
      state.exportPatents = initialState.exportPatents;
    },
    setColumnsToExport: (state, action: PayloadAction<string>) => {
      if (state.exportColumns.includes(action.payload)) {
        state.exportColumns = [...state.exportColumns.filter(i => i !== action.payload)];
      } else {
        state.exportColumns.push(action.payload);
      }
    },
    clearColumnsToExport: state => {
      state.exportColumns = initialState.exportColumns;
    },
    setDrawerFilterOpen: (state, action: PayloadAction<{ open: boolean; searchId: string | null }>) => {
      state.drawerFilterOpen = action.payload.open;
      state.drawerFilterId = action.payload.searchId;
      if (action.payload.open) {
        state.lastOpenedDrawer = 'filter';
      }
    },
    setDrawerUserInfoOpen: (state, action: PayloadAction<{ open: boolean }>) => {
      state.drawerUserInfoOpen = action.payload.open;
      if (action.payload.open) {
        state.lastOpenedDrawer = 'userInfo';
      }
    },
    setDrawerSuggestionsOpen: (state, action: PayloadAction<{ open: boolean }>) => {
      state.drawerSuggestionsOpen = action.payload.open;
      if (action.payload.open) {
        state.lastOpenedDrawer = 'suggestions';
      }
    },
    setToggleDrawerSuggestions: state => {
      state.drawerSuggestionsOpen = !state.drawerSuggestionsOpen;
      if (state.drawerSuggestionsOpen) {
        state.lastOpenedDrawer = 'suggestions';
      }
    },
    setSearchFromHistory: (
      state,
      action: PayloadAction<{
        enabled: boolean;
        searchID: string;
        searchFilters: { field: string; values: string[] }[];
        dateRangeFilters: {
          filterField: string;
          fromDate: string;
          toDate: string;
        }[];
        prefixFilters: { field: string; value: string }[];
        patentId: string | null;
      }>,
    ) => {
      state.searchFromHistoryEnabled = action.payload.enabled;
      state.searchFromHistoryID = action.payload.searchID;
      state.searchFromHistoryFilters = action.payload.searchFilters;
      state.searchFromHistoryDateRangeFilters = action.payload.dateRangeFilters;
      state.searchFromHistoryCPCFilters = action.payload.prefixFilters;
      state.searchFromHistoryPatentId = action.payload.patentId;
    },
    resetPatentId: state => {
      state.searchFromHistoryPatentId = null;
    },
    resetAppSlice: () => initialState,
    setSearchType: (state, action: PayloadAction<'patent' | 'chemistry'>) => {
      state.searchType = action.payload;
    },
    setPatentabilityAnalysisDrawerOpen: (state, action: PayloadAction<{ open: boolean }>) => {
      state.patentabilityAnalysisDrawerOpen = action.payload.open;
      if (action.payload.open) {
        state.lastOpenedDrawer = 'patentabilityAnalysis';
      }
    },
    setTogglePatentabilityAnalysisDrawer: state => {
      state.patentabilityAnalysisDrawerOpen = !state.patentabilityAnalysisDrawerOpen;
      if (state.patentabilityAnalysisDrawerOpen) {
        state.lastOpenedDrawer = 'patentabilityAnalysis';
      }
    },
  },
});

export default appSlice.reducer;

// Action creators are generated for each case reducer function
export const {
  setDrawerFilterOpen,
  setDrawerSuggestionsOpen,
  setPatentabilityAnalysisDrawerOpen,

  resetAppSlice,

  addPatentsToExport,
  removePatentsToExport,
  setColumnsToExport,
  clearPatentToExport,
  clearColumnsToExport,

  setToggleDrawerSuggestions,

  setLastOpenedDrawer,
} = appSlice.actions;
